import 'styles/global.css';
import 'styles/toast.css';

import { init } from '@amplitude/analytics-browser';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, CircularProgress } from '@mui/material';
import { createRootRouteWithContext, HeadContent, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { ErrorBoundary } from 'components/error-boundary';
import { ErrorModal } from 'components/modals/error';
import { NotFoundError, RouteErrorBoundary } from 'components/route-errors';
import { buildRouteMeta } from 'helpers/seo';
import { useInitGrowthbook } from 'hooks/use-init-growthbook';
import { ReactQueryProvider } from 'providers/react-query';
import { MuiThemeProvider } from 'providers/theme';
import type { JSX } from 'react';
import { Suspense, useEffect, useLayoutEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { useSendUserProperties } from 'router/hooks/use-send-user-properties';
import type { TRouterContext } from 'router/types';
import { useAnalytics } from 'services/analytics';
import chicApi from 'services/chic-api';
import { growthbook } from 'services/growthbook';
import { useConfigStore } from 'store';
import { AMPLITUDE_ID } from 'utils';
import { useShallow } from 'zustand/shallow';

const UiFullScreenLoader = (): JSX.Element => (
  <Box height="100vh" top="50%" left="50%" position="fixed" width="100%" justifyContent="center" alignItems="center">
    <CircularProgress size={60} />
  </Box>
);

export const Route = createRootRouteWithContext<TRouterContext>()({
  head: () => buildRouteMeta({ title: 'Lumi', description: 'Lumi web application' }),
  component: RootComponent,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: NotFoundError,
});

// Inner component that uses hooks requiring providers (React Query, etc.)
function RootContent(): JSX.Element {
  const { setConfig, setIsConfigLoading } = useConfigStore(
    useShallow((state) => ({
      setConfig: state.setConfig,
      setIsConfigLoading: state.setIsConfigLoading,
    })),
  );

  const { trackEvent } = useAnalytics();

  useInitGrowthbook();
  useSendUserProperties();

  // fetch config data
  useEffect(() => {
    setIsConfigLoading(true);

    chicApi
      .getConfig()
      .then((configData) => {
        setConfig(configData?.data);
        setIsConfigLoading(false);
      })
      .catch(() => {
        setIsConfigLoading(false);
      });
  }, [setConfig, setIsConfigLoading]);

  // initialize amplitude
  useEffect(() => {
    init(AMPLITUDE_ID);
  }, []);

  // Track page views on mount
  useLayoutEffect(() => {
    const pathname = window.location.pathname;
    if (
      !pathname.includes('/style-plan/') &&
      !pathname.includes('/style-plan') &&
      !pathname.includes('/subscription') &&
      !pathname.includes('/unsubscribe') &&
      !pathname.includes('/sign-in')
    ) {
      trackEvent(`${pathname.slice(1).replace(/\//g, '').replace('-', '_') || 'home'}_screen_view`);
    }
  }, []);

  return (
    <>
      <HeadContent />
      <Outlet />
      <ErrorModal />
      <ToastContainer
        autoClose={5000}
        position="top-center"
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        icon={(data): JSX.Element => {
          if (data.type === 'success') {
            return <TaskAltIcon color="success" />;
          }
          return <ErrorOutlineIcon />;
        }}
      />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
}

// Outer component that sets up providers (no hooks that require providers)
function RootComponent(): JSX.Element {
  return (
    <ReactQueryProvider>
      <GrowthBookProvider growthbook={growthbook}>
        <MuiThemeProvider>
          <ErrorBoundary>
            <Suspense fallback={<UiFullScreenLoader />}>
              <RootContent />
            </Suspense>
          </ErrorBoundary>
        </MuiThemeProvider>
      </GrowthBookProvider>
    </ReactQueryProvider>
  );
}
